import { DEFAULT_LOCALE, isLocale, productVariables, VENDOR, type Locale, type ProductEdition } from '@dbrain/shared';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { b2cEn, b2cTr } from './b2c.js';
import { b2cAuthEn, b2cAuthTr } from './b2c-auth.js';
import { b2cBillingEn, b2cBillingTr } from './b2c-billing.js';
import { b2cModelsEn, b2cModelsTr } from './b2c-models.js';
import { b2cPlatformEn, b2cPlatformTr } from './b2c-platform.js';
import { en } from './en.js';
import { tr } from './tr.js';

const STORAGE_KEY = 'dbrain.lang';

/** First visit: saved choice → browser language → Turkish. */
export function initialLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLocale(saved)) return saved;
  } catch {
    // storage unavailable (private mode); fall through
  }
  const browser = (navigator.language || '').slice(0, 2).toLowerCase();
  return isLocale(browser) ? browser : DEFAULT_LOCALE;
}

export function currentLocale(): Locale {
  return isLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE;
}

export async function setLocale(locale: Locale): Promise<void> {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // ignore
  }
  document.documentElement.lang = locale;
  if (i18n.language !== locale) await i18n.changeLanguage(locale);
}

// the vendor's name from its one place (D-134): {{vendor}}, and in Turkish {{vendorTo}} ("…'e gönderin")
const VENDOR_VARIABLES = { vendor: VENDOR.name, vendorTo: VENDOR.nameDativeTr };

void i18n.use(initReactI18next).init({
  // Phase 16a: the bireysel edition's words live in their own files (b2c*.ts), each under its own top-level key
  resources: {
    tr: { translation: { ...tr, ...b2cTr, ...b2cAuthTr, ...b2cPlatformTr, ...b2cBillingTr, ...b2cModelsTr } },
    en: { translation: { ...en, ...b2cEn, ...b2cAuthEn, ...b2cPlatformEn, ...b2cBillingEn, ...b2cModelsEn } },
  },
  lng: initialLocale(),
  fallbackLng: DEFAULT_LOCALE,
  // the products' names of the kurumsal edition until /api/meta names the edition (D-217): {{chatProduct}}, {{codeProductTo}}…
  interpolation: { escapeValue: false, defaultVariables: { ...VENDOR_VARIABLES, ...productVariables('kurumsal') } },
  returnNull: false,
});
document.documentElement.lang = i18n.language;

/** D-217: the products' names of this installation's edition ("Chat and Cowork" and "Code", or "Chat"), once /api/meta says which. */
export function setProductEdition(edition: ProductEdition | undefined): void {
  const interpolation = i18n.options.interpolation;
  if (interpolation) interpolation.defaultVariables = { ...VENDOR_VARIABLES, ...productVariables(edition) };
}

export default i18n;
